import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Push-to-talk audio recorder using MediaRecorder API.
 *
 * States: 'idle' | 'requesting' | 'recording' | 'processing' | 'error'
 *
 * Returns:
 *   state           — current recorder state
 *   error           — last error message ('' if none)
 *   start()         — request mic and begin recording
 *   stop()          — stop recording; resolves with a Blob (or null on cancel)
 *   cancel()        — drop the current recording without uploading
 *   isSupported     — true if MediaRecorder is available in this browser
 *   elapsedSeconds  — seconds since the recording started (0 when idle)
 */
export default function useVoiceRecorder() {
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);
  const resolveRef = useRef(null);

  const isSupported =
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof window.MediaRecorder !== 'undefined';

  const cleanup = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setElapsedSeconds(0);
  }, []);

  // Stop tracks if the component unmounts mid-recording
  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (!isSupported) {
      setError('unsupported');
      setState('error');
      return false;
    }
    setError('');
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Choose a mime type the browser supports — order from most-compatible.
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        '',
      ];
      const mimeType =
        candidates.find((m) => !m || window.MediaRecorder.isTypeSupported(m)) ?? '';
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        if (resolveRef.current) {
          resolveRef.current(blob);
          resolveRef.current = null;
        }
        cleanup();
      };

      recorder.start();
      startTsRef.current = Date.now();
      tickRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTsRef.current) / 1000));
      }, 250);
      setState('recording');
      return true;
    } catch (err) {
      const code = err && err.name === 'NotAllowedError' ? 'permission' : 'mic_error';
      setError(code);
      setState('error');
      cleanup();
      return false;
    }
  }, [cleanup, isSupported]);

  const stop = useCallback(
    () =>
      new Promise((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
          resolve(null);
          return;
        }
        resolveRef.current = resolve;
        setState('processing');
        recorder.stop();
      }),
    []
  );

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Drop chunks before stop fires
      chunksRef.current = [];
      try {
        recorder.stop();
      } catch (e) {
        /* noop */
      }
    }
    setState('idle');
    cleanup();
  }, [cleanup]);

  return {
    state,
    error,
    start,
    stop,
    cancel,
    isSupported,
    elapsedSeconds,
  };
}
